import useLocale from 'next-translate/useTranslation';

/**
 * The page's `t`. A key that a locale hasn't translated yet falls back to the
 * English key with its {{placeholders}} filled in: next-translate otherwise
 * returns a missing key raw, placeholders and all.
 */
export function useAboutTranslation() {
  const { t, lang } = useLocale('about-us');
  return {
    lang,
    t: (key: string, query?: Record<string, unknown>) =>
      t(key, query, { default: key }),
  };
}

/** Intl locale for the page's language, with Latin digits in Arabic. */
export function intlLocale(lang: string) {
  return lang === 'ar' ? 'ar-u-nu-latn' : lang;
}

/** "Sep 2022" in the reader's language, from "2022-09" (or just "2024"). */
export function formatMonth(value: string, lang: string) {
  const [year, month] = value.split('-').map(Number);
  if (!month) return String(year);
  return new Intl.DateTimeFormat(intlLocale(lang), {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, 1)));
}

export const TRUST_CENTER_URL = 'https://easypeasyai.trust.site/';

/** Public sources for the milestones on the page. */
export const SOURCES = {
  cybernews: 'https://cybernews.com/ai-news/asia-ai-trustworthiness-2026/',
  e27: 'https://e27.co/asias-ai-trust-gap-strong-transparency-weak-security-and-unclear-data-practices-20260826/',
  ascii: 'https://ascii.jp/elem/000/004/122/4122868/',
  aseanStartupAwards: 'https://aseanstartupawards.com/edition10/',
  brandsForGood:
    'https://www.brandsforgood.asia/2025-winners/easy-peasy-ai-marianna-olefyrenko',
} as const;

// Pre-sized WebP copies in R2 (bucket "images"), served as-is by Cloudflare:
// render them with `unoptimized` so Vercel never resizes or serves them.
const IMG = 'https://media.easy-peasy.ai/landing/about';

/** Photos from our own events, awards and workshops. */
export const PHOTOS = {
  foundersAtGoogle: `${IMG}/founders-google-2025.webp`,
  aseanWinner: `${IMG}/asean-startup-awards-2024.webp`,
  sme500: `${IMG}/sme-500-award-2024.webp`,
  brandsForGood2025: `${IMG}/brands-for-good-2025.webp`,
  brandsForGood2026: `${IMG}/brands-for-good-2026.webp`,
  googleDemoDay: `${IMG}/google-ai-first-demo-day-2025.webp`,
  marianna: `${IMG}/marianna.webp`,
  dmytro: `${IMG}/dmytro.webp`,
  awsArchitect: `${IMG}/aws-solutions-architect-associate.webp`,
  awsGenAi: `${IMG}/aws-generative-ai-essentials.webp`,
  mariannaTeaching: `${IMG}/marianna-teaching.webp`,
  capitaLand: `${IMG}/capitaland-workshop.webp`,
  workshopJune: `${IMG}/workshop-june-2025.webp`,
  marky: `${IMG}/marky.webp`,
} as const;
